import { getAccessToken } from '@/api/amplify/helpers';
import { AccountPackageApp } from '@/api/billing';
import { StudioVideoStatus } from '@/features/ai-video-creator/stores/studioVideoStore';
import { MediaAsset, StandaloneMediaAsset } from '@/types/media';
import { PersonalAvatarStatus } from '@/types/avatar';
import { Shot } from '@/features/video-generation-stepper/inputs/multicutShotsEditor/MulticutShotsEditor';
import { TemplateName } from '@/features/studio-video-templates/types/template';

type NotificationChannelEvents = {
  'account-package-updated': {
    packageId: string;
    subscriptionCredits?: number;
    extraCredits?: number;
    app: AccountPackageApp;
  };

  'studio-video-generation': {
    id: string;
    status: StudioVideoStatus;
  };
  'media-asset-generation': StandaloneMediaAsset;
  'personal-avatar-generation': {
    id: string;
    status: PersonalAvatarStatus;
  };
  'select-multi-images-result': {
    images: Record<string, MediaAsset[]>;
  };
};

type OutgoingNotificationChannelEvents = {
  'select-multi-images': {
    appKey: string;
    appUrl: string;
    productIds: string[];
    shots?: Shot[];
    useCaseType: TemplateName;
  };
};

export class NotificationChannelClient {
  private socket: WebSocket | null = null;
  private subscriptions: {
    [K in keyof NotificationChannelEvents]: Set<
      (data: NotificationChannelEvents[K], { requestId }: { requestId?: string }) => void
    >;
  } = {
    'account-package-updated': new Set(),
    'studio-video-generation': new Set(),
    'media-asset-generation': new Set(),
    'personal-avatar-generation': new Set(),
    'select-multi-images-result': new Set(),
  };
  private retryTimeout: number = 3000;
  private intentionalDisconnect: boolean = false;

  private async buildUrl(appKey: string) {
    const accessToken = await getAccessToken();
    if (!accessToken) {
      throw new Error('NotificationChannelClient: invalid access token');
    }
    const url = new URL(process.env.NEXT_PUBLIC_WSS_URL!);
    const searchParams = new URLSearchParams();
    searchParams.set('appKey', appKey);
    searchParams.set('token', accessToken.toString());
    url.search = searchParams.toString();
    return url.toString();
  }

  public async connect(appKey: string) {
    if (this.socket) {
      console.warn('NotificationChannelClient:Already connected');
      return;
    }
    this.intentionalDisconnect = false;
    const url = await this.buildUrl(appKey);
    this.socket = new WebSocket(url);

    this.socket.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data) as {
          appKey: string;
          topic: keyof NotificationChannelEvents;
          data: NotificationChannelEvents[keyof NotificationChannelEvents];
          requestId?: string;
        };
        const callbacks = this.subscriptions[message.topic];
        if (callbacks) {
          callbacks.forEach((callback) => callback(message.data, { requestId: message.requestId }));
        }
      } catch (error) {
        console.error('NotificationChannelClient: error parsing message:', error);
      }
    };

    this.socket.onopen = () => {
      console.log('NotificationChannelClient: connected');
    };

    this.socket.onclose = () => {
      console.log('NotificationChannelClient: disconnected');
      this.socket = null;
      if (!this.intentionalDisconnect) {
        setTimeout(() => this.connect(appKey), this.retryTimeout);
      }
    };

    this.socket.onerror = (error) => {
      console.error('NotificationChannelClient: WebSocket error:', error);
    };
  }

  public disconnect() {
    if (!this.socket) {
      console.warn('NotificationChannelClient: not connected');
      return;
    }

    this.intentionalDisconnect = true;
    this.socket.close();
    this.socket = null;
  }

  public subscribe<T extends keyof NotificationChannelEvents>(
    topic: T,
    callback: (data: NotificationChannelEvents[T], { requestId }: { requestId?: string }) => void
  ): () => void {
    const callbacks = this.subscriptions[topic];
    callbacks.add(callback);

    return () => {
      callbacks.delete(callback);
    };
  }

  public send<T extends keyof OutgoingNotificationChannelEvents>(
    topic: T,
    payload: OutgoingNotificationChannelEvents[T]
  ) {
    if (!this.socket) {
      console.warn('NotificationChannelClient: not connected');
      return;
    }
    const requestId = crypto.randomUUID();
    this.socket.send(JSON.stringify({ topic, payload, requestId }));

    return requestId;
  }
}

export const notificationChannelClient = new NotificationChannelClient();
